/**
 * Custom Filtering Enhancement
 * 
 * This script fixes the issue where filtered products remain hidden until scrolled.
 * It extends the existing filtering functionality without modifying core theme files.
 * 
 * This approach ensures the fix survives theme updates.
 */

class FilteringEnhancement {
    constructor() {
        this.debugEnabled = true; // Set to false to disable debug logs
        this.logPrefix = '[FilteringFix]';
        this.init();
    }

    log(...args) {
        if (this.debugEnabled) {
            console.log(this.logPrefix, ...args);
        }
    }

    warn(...args) {
        if (this.debugEnabled) {
            console.warn(this.logPrefix, ...args);
        }
    }

    error(...args) {
        if (this.debugEnabled) {
            console.error(this.logPrefix, ...args);
        }
    }

    init() {
        this.log('Initializing FilteringEnhancement...');

        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            this.log('DOM still loading, waiting for DOMContentLoaded...');
            document.addEventListener('DOMContentLoaded', () => this.setupEnhancement());
        } else {
            this.log('DOM ready, setting up enhancement immediately...');
            this.setupEnhancement();
        }
    }

    setupEnhancement() {
        this.log('Setting up filtering enhancement...');

        // Listen for the custom 'paginate' event that's fired after filtering
        document.addEventListener('paginate', (event) => {
            this.log('Paginate event detected:', event);
            this.triggerShowOnScrollAnimations();
        });
        this.log('Added paginate event listener');

        // Also listen for DOMContentLoaded as an additional fallback
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.log('DOMContentLoaded fired, checking for elements');
                this.triggerShowOnScrollAnimations();
            });
        }

        // Listen for any custom events that might indicate content changes
        document.addEventListener('shopify:section:load', (event) => {
            this.log('Shopify section load event detected:', event);
            this.triggerShowOnScrollAnimations();
        });

        // Also listen for any DOM changes in the product grid (fallback)
        this.observeProductGridChanges();

        this.log('FilteringEnhancement setup complete');

        // Expose a global function for manual testing
        window.testFilteringFix = () => {
            this.log('Manual test triggered');
            this.triggerShowOnScrollAnimations();
        };

        // Run initial check after a short delay
        setTimeout(() => {
            this.log('Running initial check for show-on-scroll elements');
            this.triggerShowOnScrollAnimations();
        }, 1000);
    }

    triggerShowOnScrollAnimations() {
        this.log('Triggering show-on-scroll animations...');
        this.log('Document readyState:', document.readyState);
        this.log('Current URL:', window.location.href);

        // Check for collection grid immediately
        const collectionGrid = document.getElementById('CollectionProductGrid');
        this.log('CollectionProductGrid found:', !!collectionGrid);
        if (collectionGrid) {
            this.log('CollectionProductGrid innerHTML length:', collectionGrid.innerHTML.length);
        }

        // Small delay to ensure DOM is updated
        setTimeout(() => {
            const showOnScrollElements = document.querySelectorAll('.show-on-scroll');
            const allGridItems = document.querySelectorAll('.grid__item');

            this.log(`Found ${showOnScrollElements.length} elements with show-on-scroll class`);
            this.log(`Found ${allGridItems.length} total grid items`);

            if (showOnScrollElements.length === 0) {
                this.log('No show-on-scroll elements found. Checking for alternatives...');
                const alternatives = {
                    '.grid__item.show-on-scroll': document.querySelectorAll('.grid__item.show-on-scroll').length,
                    '.show-on-scroll.grid__item': document.querySelectorAll('.show-on-scroll.grid__item').length,
                    '[class*="show-on-scroll"]': document.querySelectorAll('[class*="show-on-scroll"]').length
                };
                this.log('Alternative selectors:', alternatives);
            }

            let animatedCount = 0;
            showOnScrollElements.forEach((element, index) => {
                const rect = element.getBoundingClientRect();
                const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

                this.log(`Element ${index + 1}:`, {
                    element,
                    classes: element.className,
                    rect: { top: rect.top, bottom: rect.bottom },
                    windowHeight: window.innerHeight,
                    isVisible
                });

                if (isVisible) {
                    this.log(`Animating element ${index + 1}`);
                    element.classList.remove('show-on-scroll');
                    element.classList.add('shown-on-scroll', 'animated', 'fadeIn');
                    animatedCount++;
                }
            });

            this.log(`Immediately animated ${animatedCount} visible elements`);

            // Fix placeholder images after filtering
            this.fixPlaceholderImages();

            // Set up intersection observer for remaining elements
            this.setupIntersectionObserver();
        }, 50);
    }

    fixPlaceholderImages() {
        this.log('Fixing placeholder images...');

        // Count SVG placeholders before triggering replacement
        const svgPlaceholders = document.querySelectorAll('svg.icon--placeholder, svg[class*="icon--placeholder--"]');
        this.log(`Found ${svgPlaceholders.length} SVG placeholders to potentially replace`);

        // Trigger a custom event to reuse the existing placeholder replacement logic
        // from override-placeholders.liquid instead of duplicating the code
        const placeholderEvent = new CustomEvent('replacePlaceholders');
        this.log('Dispatching replacePlaceholders event:', placeholderEvent);
        document.dispatchEvent(placeholderEvent);

        // Small delay to check results
        setTimeout(() => {
            const remainingSvgPlaceholders = document.querySelectorAll('svg.icon--placeholder, svg[class*="icon--placeholder--"]');
            const replacedCount = svgPlaceholders.length - remainingSvgPlaceholders.length;
            this.log(`Placeholder replacement complete. ${replacedCount} SVGs replaced, ${remainingSvgPlaceholders.length} remaining`);
        }, 10);
    }

    setupIntersectionObserver() {
        const remainingElements = document.querySelectorAll('.show-on-scroll');
        this.log(`Setting up intersection observer for ${remainingElements.length} remaining elements`);

        if (remainingElements.length > 0) {
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.log('Element became visible via intersection observer:', entry.target);
                        entry.target.classList.remove('show-on-scroll');
                        entry.target.classList.add('shown-on-scroll', 'animated', 'fadeIn');
                        observer.unobserve(entry.target);
                    }
                });
            });

            remainingElements.forEach((element, index) => {
                observer.observe(element);
                this.log(`Added element ${index + 1} to intersection observer`);
            });

            this.log('Intersection observer setup complete');
        } else {
            this.log('No remaining elements need intersection observer');
        }
    }

    observeProductGridChanges() {
        const productGrid = document.getElementById('CollectionProductGrid');
        if (!productGrid) {
            this.warn('CollectionProductGrid not found, mutation observer not set up');
            return;
        }

        this.log('Setting up mutation observer for product grid changes...');

        const observer = new MutationObserver(mutations => {
            this.log(`Mutation observer detected ${mutations.length} mutations`);

            mutations.forEach((mutation, index) => {
                this.log(`Mutation ${index + 1}:`, {
                    type: mutation.type,
                    addedNodes: mutation.addedNodes.length,
                    removedNodes: mutation.removedNodes.length,
                    target: mutation.target
                });

                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // Check if new product items were added
                    const hasNewProducts = Array.from(mutation.addedNodes).some(node => {
                        const isElement = node.nodeType === Node.ELEMENT_NODE;
                        const hasGridItem = isElement && (node.classList?.contains('grid__item') || node.querySelector?.('.grid__item'));

                        this.log('Checking added node:', {
                            node,
                            isElement,
                            hasGridItem,
                            classList: node.classList ? Array.from(node.classList) : 'no classList'
                        });

                        return hasGridItem;
                    });

                    if (hasNewProducts) {
                        this.log('New product items detected via mutation observer, triggering animations');
                        this.triggerShowOnScrollAnimations();
                    }
                }
            });
        });

        observer.observe(productGrid, {
            childList: true,
            subtree: true
        });

        this.log('Mutation observer setup complete for CollectionProductGrid');
    }
}

// Initialize the enhancement
new FilteringEnhancement();
